import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { X, Minimize2, Maximize2, Move, Pin, PinOff, Film, Eye, EyeOff } from 'lucide-react';
import DropboxIcon from './DropboxIcon';
import { TtmlMetadataService, type StructuredClip, type StructuredTopic } from '../services/ttmlMetadataService';
import { listPublicShareQuestions, listQuestions, type SessionQuestion } from '../services/questionService';
import { getTopicRankingColor } from '../contexts/TopicVisualizationContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import { getSpeakerColorValue } from '../utils/speakerColors';

interface HostedVideoPlayerProps {
  videoUrl: string;
  sessionId?: string | null;
  selectedTtmlChecksum?: string | null;
  selectedTranscriptChecksum?: string | null;
  totalDuration?: number;
  token?: string;
  speakerSegments?: Map<number, any[]>;
  speakerLabels?: Record<number, string>;
  isVisible: boolean;
  onClose: () => void;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayStateChange: (isPlaying: boolean) => void;
  onReady: () => void;
  currentTime?: number;
  mediaSeekRequestId?: number;
  mediaSeekRequestTime?: number | null;
  isPlaying?: boolean;
  volume?: number;
  playbackRate?: number;
  isDarkMode: boolean;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  onFloatingChange?: (isFloating: boolean) => void;
  isMobile?: boolean;
  mobileTopicTitle?: string | null;
  useExternalAudioSync?: boolean;
}

interface SubtitleCue {
  start: number;
  end: number;
  lines: string[];
  text: string;
}

interface ActiveSpeaker {
  id: number;
  label: string;
  color: string;
}

const SMALL_SPEAKER_GAP_SECONDS = 2;

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

const PUBLIC_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

const formatClockTime = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const stripHtmlText = (value?: string | null): string => (
  String(value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const parseSrtTimestamp = (value: string): number => {
  const match = String(value || '').trim().match(/(\d+):(\d+):(\d+),(\d+)/);
  if (!match) return 0;
  const [, hours, minutes, seconds, millis] = match;
  return (Number(hours) * 3600) + (Number(minutes) * 60) + Number(seconds) + (Number(millis) / 1000);
};

const parseSrtCues = (content: string): SubtitleCue[] => (
  String(content || '')
    .split(/\r?\n\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      const timeLineIndex = lines.findIndex((line) => line.includes('-->'));
      if (timeLineIndex === -1) return null;
      const [startValue, endValue] = lines[timeLineIndex].split('-->').map((part) => part.trim());
      const textLines = lines.slice(timeLineIndex + 1).map((line) => stripHtmlText(line)).filter(Boolean);
      if (!startValue || !endValue || textLines.length === 0) return null;
      return {
        start: parseSrtTimestamp(startValue),
        end: parseSrtTimestamp(endValue),
        lines: textLines,
        text: textLines.join(' '),
      };
    })
    .filter((cue): cue is SubtitleCue => Boolean(cue))
);

const isEditableShortcutTarget = (target: EventTarget | null): boolean => (
  target instanceof HTMLElement
  && !!target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')
);

const normalizeVideoUrl = (url: string): string => {
  if (!url) return '';
  if (!url.includes('dropbox.com')) {
    return url;
  }

  let rawUrl = url;
  if (rawUrl.includes('dl=0')) {
    rawUrl = rawUrl.replace('dl=0', 'dl=1');
  } else if (!rawUrl.includes('dl=1')) {
    rawUrl = rawUrl.includes('?') ? `${rawUrl}&dl=1` : `${rawUrl}?dl=1`;
  }

  console.log('[HOSTED-VIDEO] Normalized Dropbox URL:', rawUrl);
  return rawUrl;
};

const findSpeakerAtPlaybackTime = (
  speakerSegments: Map<number, any[]> | undefined,
  playbackTime: number
): { speakerId: number; segmentStart: number; segmentEnd: number } | null => {
  if (!speakerSegments || speakerSegments.size === 0 || !Number.isFinite(playbackTime)) {
    return null;
  }

  const flattenedSegments = Array.from(speakerSegments.entries())
    .flatMap(([speakerId, segments]) => (
      Array.isArray(segments)
        ? segments
            .filter((segment) => Number.isFinite(segment?.start) && Number.isFinite(segment?.end))
            .map((segment) => ({
              speakerId: Number(speakerId),
              segmentStart: Number(segment.start),
              segmentEnd: Number(segment.end),
            }))
        : []
    ))
    .sort((left, right) => left.segmentStart - right.segmentStart);

  const activeSegment = flattenedSegments.find((segment) => (
    playbackTime >= segment.segmentStart && playbackTime < segment.segmentEnd
  ));
  if (activeSegment) {
    return activeSegment;
  }

  let previousSegment: typeof flattenedSegments[number] | null = null;
  let nextSegment: typeof flattenedSegments[number] | null = null;

  for (const segment of flattenedSegments) {
    if (segment.segmentEnd <= playbackTime) {
      previousSegment = segment;
      continue;
    }

    if (segment.segmentStart > playbackTime) {
      nextSegment = segment;
      break;
    }
  }

  if (
    previousSegment
    && nextSegment
    && playbackTime >= previousSegment.segmentEnd
    && playbackTime < nextSegment.segmentStart
    && (nextSegment.segmentStart - previousSegment.segmentEnd) <= SMALL_SPEAKER_GAP_SECONDS
  ) {
    return previousSegment;
  }

  return null;
};

const HostedVideoPlayer: React.FC<HostedVideoPlayerProps> = ({
  videoUrl,
  sessionId = null,
  selectedTtmlChecksum = null,
  selectedTranscriptChecksum = null,
  totalDuration,
  token,
  speakerSegments,
  speakerLabels,
  isVisible,
  onClose,
  onTimeUpdate,
  onDurationChange,
  onPlayStateChange,
  onReady,
  currentTime,
  mediaSeekRequestId = 0,
  mediaSeekRequestTime = null,
  isPlaying,
  volume = 1,
  playbackRate = 1,
  isDarkMode,
  isMinimized = false,
  onToggleMinimize,
  onFloatingChange,
  isMobile = false,
  mobileTopicTitle = null,
  useExternalAudioSync = false,
}) => {
  const { permissions: sharePermissions, shareToken } = useShareLink();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenTargetRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMetadataOverlay, setShowMetadataOverlay] = useState(false);
  const [overlayBounds, setOverlayBounds] = useState({ width: 0, height: 0 });
  const [structuredTopics, setStructuredTopics] = useState<StructuredTopic[]>([]);
  const [structuredClips, setStructuredClips] = useState<StructuredClip[]>([]);
  const [questionData, setQuestionData] = useState<SessionQuestion[]>([]);
  const [subtitleCues, setSubtitleCues] = useState<SubtitleCue[]>([]);
  const lastRequestedSeekRef = useRef<number | null>(null);
  const lastObservedTimeRef = useRef<number | null>(null);
  const lastProgrammaticSeekAtRef = useRef(0);
  const lastProgrammaticPlaybackAtRef = useRef(0);
  const lastHandledSeekRequestIdRef = useRef(0);
  const isSeekingRef = useRef(false);
  const shouldBePlayingRef = useRef(false);
  const pendingSyncTargetRef = useRef<number | null>(null);
  const playbackWarmRef = useRef(false);
  const isDropboxSource = videoUrl.includes('dropbox.com');
  const supportsHostedFullscreenOverlay = !isDropboxSource;
  const rawUrl = normalizeVideoUrl(videoUrl);
  const playbackTime = Number.isFinite(currentTime as number) ? Number(currentTime) : 0;

  // Store callbacks in refs to avoid effect re-runs
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onDurationChangeRef = useRef(onDurationChange);
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const onReadyRef = useRef(onReady);

  // Update refs when callbacks change
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);
  useEffect(() => { onDurationChangeRef.current = onDurationChange; }, [onDurationChange]);
  useEffect(() => { onPlayStateChangeRef.current = onPlayStateChange; }, [onPlayStateChange]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  // Floating state
  const [isFloating, setIsFloating] = useState(false);
  const [floatPosition, setFloatPosition] = useState({ x: 20, y: 100 });
  const [floatSize, setFloatSize] = useState({ width: 400, height: 225 }); // 16:9 ratio
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ x: 0, y: 0 });
  const sizeStartRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    if (isMobile) {
      setIsFloating(false);
    }
  }, [isMobile]);

  // Notify parent when floating state changes
  useEffect(() => {
    onFloatingChange?.(isFloating);
  }, [isFloating, onFloatingChange]);

  useEffect(() => {
    if (!isVisible || !supportsHostedFullscreenOverlay || !sessionId) {
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const loadOverlayData = async () => {
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const isPublicShare = sharePermissions?.isPublicShare === true && Boolean(shareToken);

      const srtParams = new URLSearchParams();
      if (selectedTranscriptChecksum) {
        srtParams.set('revision', selectedTranscriptChecksum);
      }

      const [structure, questions, srtContent] = await Promise.all([
        selectedTtmlChecksum
          ? (
            isPublicShare
              ? TtmlMetadataService.getPublicStructure(shareToken as string, selectedTtmlChecksum)
              : TtmlMetadataService.getStructure(sessionId, selectedTtmlChecksum, token)
          ).catch((err) => {
            console.warn('[HOSTED-VIDEO] Failed to load structured TTML overlay data:', err);
            return null;
          })
          : Promise.resolve(null),
        (
          isPublicShare
            ? (
              sharePermissions?.questionsEnabled
                ? listPublicShareQuestions(shareToken as string)
                : Promise.resolve({ questions: [] })
            )
            : listQuestions(sessionId, token)
        )
          .then((response) => response.questions)
          .catch((err) => {
            console.warn('[HOSTED-VIDEO] Failed to load session questions for overlay:', err);
            return [];
          }),
        fetch(
          `${
            isPublicShare
              ? `${PUBLIC_API_BASE_URL}/public/share/${encodeURIComponent(shareToken as string)}/transcript/srt`
              : `${API_BASE_URL}/episodes/${sessionId}/transcript/srt`
          }${srtParams.toString() ? `?${srtParams.toString()}` : ''}`,
          {
            headers: isPublicShare ? undefined : headers,
            credentials: isPublicShare ? 'omit' : undefined,
            signal: controller.signal,
          }
        )
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load SRT (${response.status})`);
            }
            return response.text();
          })
          .catch((err) => {
            if (!controller.signal.aborted) {
              console.warn('[HOSTED-VIDEO] Failed to load SRT captions for overlay:', err);
            }
            return '';
          }),
      ]);

      if (cancelled) return;

      setStructuredTopics(structure?.topics || []);
      setStructuredClips(structure?.clips || []);
      setQuestionData(
        [...questions]
          .filter((question) => Number.isFinite(question.timestampSeconds))
          .sort((left, right) => (left.timestampSeconds || 0) - (right.timestampSeconds || 0))
      );
      setSubtitleCues(parseSrtCues(srtContent));
    };

    void loadOverlayData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isVisible, selectedTtmlChecksum, selectedTranscriptChecksum, sessionId, sharePermissions?.isPublicShare, sharePermissions?.questionsEnabled, shareToken, supportsHostedFullscreenOverlay, token]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenTargetRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const target = fullscreenTargetRef.current;
    if (!target) {
      return;
    }

    const updateBounds = (width: number, height: number) => {
      setOverlayBounds((previous) => (
        previous.width === width && previous.height === height
          ? previous
          : { width, height }
      ));
    };

    updateBounds(target.clientWidth, target.clientHeight);

    if (typeof ResizeObserver === 'undefined') {
      const handleWindowResize = () => {
        updateBounds(target.clientWidth, target.clientHeight);
      };
      window.addEventListener('resize', handleWindowResize);
      return () => {
        window.removeEventListener('resize', handleWindowResize);
      };
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      updateBounds(entry.contentRect.width, entry.contentRect.height);
    });

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [isFullscreen, isMinimized, isVisible]);

  const toggleFullscreen = useCallback(async () => {
    if (!supportsHostedFullscreenOverlay) return;

    const target = fullscreenTargetRef.current;
    if (!target) return;

    try {
      if (document.fullscreenElement === target) {
        await document.exitFullscreen();
      } else {
        await target.requestFullscreen();
      }
    } catch (err) {
      console.error('[HOSTED-VIDEO] Failed to toggle fullscreen:', err);
    }
  }, [supportsHostedFullscreenOverlay]);

  useEffect(() => {
    if (!isVisible || !supportsHostedFullscreenOverlay || isMobile) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || isEditableShortcutTarget(event.target)) {
        return;
      }
      if (event.key.toLowerCase() !== 'f') {
        return;
      }
      event.preventDefault();
      void toggleFullscreen();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isMobile, isVisible, supportsHostedFullscreenOverlay, toggleFullscreen]);

  const resolvedTotalDuration = Number.isFinite(totalDuration as number) && Number(totalDuration) > 0
    ? Number(totalDuration)
    : (Number.isFinite(videoRef.current?.duration) ? Number(videoRef.current?.duration) : 0);
  const activeSpeaker = useMemo<ActiveSpeaker | null>(() => {
    const matchedSpeaker = findSpeakerAtPlaybackTime(speakerSegments, playbackTime);
    if (!matchedSpeaker) {
      return null;
    }

    return {
      id: matchedSpeaker.speakerId,
      label: speakerLabels?.[matchedSpeaker.speakerId] || `Speaker ${matchedSpeaker.speakerId}`,
      color: getSpeakerColorValue(matchedSpeaker.speakerId, isDarkMode),
    };
  }, [isDarkMode, playbackTime, speakerLabels, speakerSegments]);

  const activeTopic = useMemo(
    () => structuredTopics.find((topic) => playbackTime >= topic.startTime && playbackTime <= topic.endTime) || null,
    [playbackTime, structuredTopics]
  );
  const activeTopicDescription = useMemo(
    () => stripHtmlText(activeTopic?.justification || null),
    [activeTopic]
  );
  const activeClip = useMemo(
    () => structuredClips.find((clip) => playbackTime >= clip.startTime && playbackTime <= clip.endTime) || null,
    [playbackTime, structuredClips]
  );
  const activeQuestion = useMemo(() => {
    for (let index = 0; index < questionData.length; index += 1) {
      const currentQuestion = questionData[index];
      const questionStart = Number(currentQuestion.timestampSeconds);
      const nextQuestionStart = index < questionData.length - 1
        ? Number(questionData[index + 1].timestampSeconds)
        : Number.POSITIVE_INFINITY;

      if (playbackTime >= questionStart && playbackTime < nextQuestionStart) {
        return currentQuestion;
      }
    }
    return null;
  }, [playbackTime, questionData]);
  const activeSubtitleCue = useMemo(
    () => subtitleCues.find((cue) => playbackTime >= cue.start && playbackTime <= cue.end) || null,
    [playbackTime, subtitleCues]
  );
  const shouldRenderMetadataOverlay = supportsHostedFullscreenOverlay && (isFullscreen || showMetadataOverlay);
  const overlayProfile = useMemo(() => {
    const width = overlayBounds.width || (isFullscreen ? 1280 : floatSize.width);
    const compact = !isFullscreen && width > 0 && width < 520;
    const narrow = !isFullscreen && width > 0 && width < 760;

    return {
      compact,
      narrow,
      topPaddingClass: isFullscreen ? 'p-4' : compact ? 'p-2' : narrow ? 'p-3' : 'p-4',
      infoCardClass: isFullscreen
        ? 'rounded-2xl px-4 py-3'
        : compact
          ? 'rounded-xl px-2.5 py-2'
          : narrow
            ? 'rounded-xl px-3 py-2.5'
            : 'rounded-2xl px-4 py-3',
      infoLabelClass: isFullscreen ? 'text-[10px]' : compact ? 'text-[8px]' : 'text-[9px]',
      infoTitleClass: isFullscreen ? 'text-sm' : compact ? 'text-[11px]' : narrow ? 'text-xs' : 'text-sm',
      bodyTextClass: isFullscreen ? 'text-sm' : compact ? 'text-[11px]' : 'text-xs',
      metaTextClass: isFullscreen ? 'text-xs' : compact ? 'text-[10px]' : 'text-[11px]',
      timerClass: isFullscreen ? 'px-3 py-1.5 text-xs' : compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-[11px]',
      subtitleWrapperClass: isFullscreen ? 'bottom-6 px-6' : compact ? 'bottom-2 px-2' : narrow ? 'bottom-3 px-3' : 'bottom-4 px-4',
      subtitleCardClass: isFullscreen
        ? 'rounded-2xl px-5 py-3 text-lg'
        : compact
          ? 'rounded-xl px-3 py-1.5 text-[11px]'
          : narrow
            ? 'rounded-xl px-3.5 py-2 text-xs'
            : 'rounded-2xl px-4 py-2.5 text-sm',
      infoMaxWidth: Math.max(220, Math.min(width * (isFullscreen ? 0.34 : compact ? 0.78 : narrow ? 0.68 : 0.58), 440)),
      subtitleMaxWidth: Math.max(240, Math.min(width * (isFullscreen ? 0.82 : compact ? 0.94 : 0.9), isFullscreen ? 980 : 720)),
    };
  }, [floatSize.width, isFullscreen, overlayBounds.width]);

  const queuePassiveSeek = useCallback((targetTime: number, options: { force?: boolean } = {}) => {
    const video = videoRef.current;
    pendingSyncTargetRef.current = targetTime;
    if (!video || !isReady) {
      return;
    }

    const shouldApplyImmediately = options.force || !useExternalAudioSync || (
      playbackWarmRef.current && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    );
    if (!shouldApplyImmediately) {
      return;
    }

    lastRequestedSeekRef.current = targetTime;
    lastObservedTimeRef.current = targetTime;
    lastProgrammaticSeekAtRef.current = Date.now();
    isSeekingRef.current = true;
    video.currentTime = targetTime;
  }, [isReady, useExternalAudioSync]);

  const tryStartPassivePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isReady || !useExternalAudioSync) {
      return;
    }

    if (!shouldBePlayingRef.current) {
      if (!video.paused) {
        lastProgrammaticPlaybackAtRef.current = Date.now();
        video.pause();
      }
      return;
    }

    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsLoading(true);
      return;
    }

    const pendingTarget = pendingSyncTargetRef.current;
    const drift = pendingTarget === null ? 0 : Math.abs(video.currentTime - pendingTarget);
    if (pendingTarget !== null && drift > 0.3) {
      const now = Date.now();
      if (!playbackWarmRef.current || now - lastProgrammaticSeekAtRef.current < 400) {
        return;
      }
      queuePassiveSeek(pendingTarget, { force: true });
      return;
    }

    if (video.paused) {
      lastProgrammaticPlaybackAtRef.current = Date.now();
      video.play().catch((err) => {
        console.error('[HOSTED-VIDEO] Failed to play passive video:', err);
      });
    }
  }, [isReady, queuePassiveSeek, useExternalAudioSync]);

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      console.log('[HOSTED-VIDEO] Video metadata loaded, duration:', video.duration);
      if (useExternalAudioSync && Number.isFinite(currentTime) && (currentTime || 0) > 0.05) {
        const targetTime = Math.max(0, currentTime as number);
        if (Math.abs(video.currentTime - targetTime) > 0.25) {
          queuePassiveSeek(targetTime);
        }
      }
      setIsReady(true);
      setIsLoading(false);
      setError(null);
      onDurationChangeRef.current(video.duration);
      onReadyRef.current();
    };

    const handleTimeUpdate = () => {
      const now = Date.now();
      lastObservedTimeRef.current = video.currentTime;
      if (!useExternalAudioSync && !isSeekingRef.current && now - lastProgrammaticSeekAtRef.current > 150) {
        onTimeUpdateRef.current(video.currentTime);
      }
    };

    const handleSeeking = () => {
      isSeekingRef.current = true;
    };

    const handleSeeked = () => {
      const now = Date.now();
      const wasProgrammaticSeek = now - lastProgrammaticSeekAtRef.current < 500;
      isSeekingRef.current = false;
      lastObservedTimeRef.current = video.currentTime;
      if (!useExternalAudioSync && !wasProgrammaticSeek) {
        onTimeUpdateRef.current(video.currentTime);
      }
      if (useExternalAudioSync) {
        window.setTimeout(() => {
          tryStartPassivePlayback();
        }, 0);
      }
    };

    const handlePlay = () => {
      console.log('[HOSTED-VIDEO] Video playing');
      if (useExternalAudioSync) {
        return;
      }
      onPlayStateChangeRef.current(true);
    };

    const handlePause = () => {
      console.log('[HOSTED-VIDEO] Video paused');
      if (useExternalAudioSync) {
        return;
      }
      onPlayStateChangeRef.current(false);
    };

    const handleEnded = () => {
      console.log('[HOSTED-VIDEO] Video ended');
      if (useExternalAudioSync) {
        return;
      }
      onPlayStateChangeRef.current(false);
    };

    const handleError = (e: Event) => {
      console.error('[HOSTED-VIDEO] Video error:', e);
      setError('Failed to load hosted video.');
      setIsLoading(false);
    };

    const handleWaiting = () => {
      if (useExternalAudioSync) {
        playbackWarmRef.current = false;
      }
      setIsLoading(true);
    };

    const handleCanPlay = () => {
      playbackWarmRef.current = true;
      setIsLoading(false);
      if (useExternalAudioSync) {
        tryStartPassivePlayback();
      }
    };

    const handleLoadedData = () => {
      if (useExternalAudioSync) {
        tryStartPassivePlayback();
      }
    };

    const handlePlaying = () => {
      playbackWarmRef.current = true;
      setIsLoading(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [currentTime, queuePassiveSeek, rawUrl, tryStartPassivePlayback, useExternalAudioSync]);

  // Sync play/pause state from parent
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isReady) return;

    shouldBePlayingRef.current = Boolean(isPlaying);

    if (useExternalAudioSync) {
      if (!isPlaying) {
        if (!video.paused) {
          lastProgrammaticPlaybackAtRef.current = Date.now();
          video.pause();
        }
        return;
      }

      if (Number.isFinite(currentTime)) {
        pendingSyncTargetRef.current = currentTime as number;
      }
      tryStartPassivePlayback();
      return;
    }

    if (isPlaying && video.paused) {
      lastProgrammaticPlaybackAtRef.current = Date.now();
      video.play().catch(err => {
        console.error('[HOSTED-VIDEO] Failed to play:', err);
      });
    } else if (!isPlaying && !video.paused) {
      lastProgrammaticPlaybackAtRef.current = Date.now();
      video.pause();
    }
  }, [currentTime, isPlaying, isReady, tryStartPassivePlayback, useExternalAudioSync]);

  // Sync seek position from parent
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isReady || currentTime === undefined) return;

    const observedTime = lastObservedTimeRef.current;
    if (useExternalAudioSync) {
      pendingSyncTargetRef.current = currentTime;
    }
    const diff = Math.abs(video.currentTime - currentTime);
    const looksLikePlaybackEcho = !useExternalAudioSync
      && observedTime !== null
      && Math.abs(currentTime - observedTime) < 0.35;
    const wasAlreadyRequested = lastRequestedSeekRef.current !== null
      && Math.abs(lastRequestedSeekRef.current - currentTime) < 0.05;
    const seekThreshold = useExternalAudioSync
      ? (isPlaying ? 0.12 : 0.05)
      : (isPlaying ? 0.75 : 0.12);

    if (diff <= seekThreshold) return;
    if (looksLikePlaybackEcho && !wasAlreadyRequested) return;
    if (wasAlreadyRequested && diff <= (useExternalAudioSync ? 0.08 : 0.25)) return;

    console.log('[HOSTED-VIDEO] Seeking to:', currentTime);
    queuePassiveSeek(currentTime, { force: useExternalAudioSync && diff > 0.35 });
    if (useExternalAudioSync && shouldBePlayingRef.current) {
      tryStartPassivePlayback();
    }
  }, [currentTime, isPlaying, isReady, queuePassiveSeek, tryStartPassivePlayback, useExternalAudioSync]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isReady || mediaSeekRequestTime === null || mediaSeekRequestId === 0) {
      return;
    }

    if (lastHandledSeekRequestIdRef.current === mediaSeekRequestId) {
      return;
    }

    lastHandledSeekRequestIdRef.current = mediaSeekRequestId;
    queuePassiveSeek(mediaSeekRequestTime, { force: true });
    if (useExternalAudioSync && shouldBePlayingRef.current) {
      tryStartPassivePlayback();
    }
  }, [currentTime, isReady, mediaSeekRequestId, mediaSeekRequestTime, queuePassiveSeek, tryStartPassivePlayback, useExternalAudioSync]);

  // Sync volume
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = useExternalAudioSync;
    video.volume = volume;
  }, [useExternalAudioSync, volume]);

  // Sync playback rate
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  // Drag handlers for floating mode
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isFloating) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { ...floatPosition };
  }, [isFloating, floatPosition]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setFloatPosition({
        x: Math.max(0, Math.min(window.innerWidth - 320, positionStartRef.current.x + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 200, positionStartRef.current.y + dy)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Resize handler for floating mode
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isFloating) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    sizeStartRef.current = { ...floatSize };
  }, [isFloating, floatSize]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      // Maintain 16:9 aspect ratio
      const newWidth = Math.max(280, Math.min(800, sizeStartRef.current.width + dx));
      const newHeight = newWidth * (9 / 16);
      setFloatSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isVisible || !videoUrl) return null;

  // Floating mode styles
  const floatingStyles: React.CSSProperties = isFloating ? {
    position: 'fixed',
    left: floatPosition.x,
    top: floatPosition.y,
    width: isMinimized ? '200px' : `${floatSize.width}px`,
    zIndex: 1000,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    borderRadius: '8px',
    overflow: 'hidden',
  } : {};

  // Docked mode styles
  const dockedStyles: React.CSSProperties = !isFloating ? {
    width: '100%',
    maxWidth: isMinimized ? '200px' : undefined,
    height: isMinimized ? 'auto' : 'fit-content',
  } : {};

  return (
    <div
      ref={containerRef}
      style={{ ...floatingStyles, ...dockedStyles }}
      className={`flex flex-col relative ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      } ${isFloating ? 'border-2' : 'border-r'}`}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-2 py-1.5 border-b ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        } ${isFloating ? 'cursor-move' : ''}`}
        onMouseDown={handleMouseDown}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          {isFloating && <Move size={12} className="text-gray-400" />}
          {isDropboxSource && (
            <>
              <DropboxIcon size={14} className="text-blue-500" />
              <span className={`shrink-0 text-xs font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Dropbox Video
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {supportsHostedFullscreenOverlay && (
            <button
              onClick={() => setShowMetadataOverlay((previous) => !previous)}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 transition-colors ${
                isDarkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              title={isFullscreen ? 'Overlay is always enabled in fullscreen' : (showMetadataOverlay ? 'Hide metadata overlay' : 'Show metadata overlay')}
              disabled={isFullscreen}
            >
              {(showMetadataOverlay || isFullscreen) ? <Eye size={12} /> : <EyeOff size={12} />}
              <span className="hidden text-[11px] font-medium sm:inline">
                {(showMetadataOverlay || isFullscreen) ? 'Overlay on' : 'Overlay off'}
              </span>
            </button>
          )}
          {!isMobile && (
            <>
              {supportsHostedFullscreenOverlay && (
                <button
                  onClick={() => { void toggleFullscreen(); }}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 transition-colors ${
                    isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
                >
                  {isFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                  <span className="hidden text-[11px] font-medium sm:inline">
                    {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  </span>
                  <span className={`rounded px-1 py-0.5 text-[10px] font-mono ${
                    isDarkMode ? 'bg-white/10 text-gray-200' : 'bg-black/[0.06] text-gray-700'
                  }`}>
                    F
                  </span>
                </button>
              )}
              <button
                onClick={() => setIsFloating(!isFloating)}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
                title={isFloating ? 'Dock to sidebar' : 'Float window'}
              >
                {isFloating ? <Pin size={12} /> : <PinOff size={12} />}
              </button>
              {onToggleMinimize && (
                <button
                  onClick={onToggleMinimize}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                  title={isMinimized ? 'Expand' : 'Minimize'}
                >
                  {isMinimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                </button>
              )}
            </>
          )}
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
            title="Close video player"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Player container */}
      {!isMinimized && (
        <div
          ref={fullscreenTargetRef}
          className="relative bg-black"
          style={isFullscreen ? {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          } : {
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden'
          }}
        >
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-red-500 px-4">
                {isDropboxSource ? (
                  <DropboxIcon size={32} className="mx-auto mb-2 opacity-50" />
                ) : (
                  <Film size={32} className="mx-auto mb-2 opacity-50" />
                )}
                <p className="text-xs">{error}</p>
              </div>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={rawUrl}
                className={`absolute inset-0 pointer-events-none select-none ${isFullscreen ? 'h-full w-full object-contain' : 'h-full w-full'}`}
                playsInline
                muted={useExternalAudioSync}
                controls={false}
                controlsList="nodownload nofullscreen noplaybackrate noremoteplayback"
                disablePictureInPicture
                tabIndex={-1}
                onContextMenu={(event) => event.preventDefault()}
                preload="auto"
              />
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="text-center text-white">
                    <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-2" />
                    <p className="text-xs">{useExternalAudioSync ? 'Syncing video… audio stays live' : 'Loading…'}</p>
                  </div>
                </div>
              )}
              {shouldRenderMetadataOverlay && (
                <>
                  <div className={`pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 ${overlayProfile.topPaddingClass}`}>
                    <div
                      className={`border border-white/15 bg-[#0B1320]/70 text-[#F4F4F4] shadow-[0_24px_48px_-30px_rgba(0,0,0,0.72)] backdrop-blur-md ${overlayProfile.infoCardClass}`}
                      style={{ maxWidth: `${overlayProfile.infoMaxWidth}px` }}
                    >
                      {activeSpeaker && (
                        <div className="mb-2">
                          <div className={`font-body font-semibold uppercase tracking-[0.22em] text-[#FFB52B]/80 ${overlayProfile.infoLabelClass}`}>
                            Active Speaker
                          </div>
                          <div className={`mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 font-medium text-white ${overlayProfile.metaTextClass}`}>
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: activeSpeaker.color }}
                            />
                            <span>{activeSpeaker.label}</span>
                          </div>
                        </div>
                      )}
                      {activeTopic && (
                        <div className="mb-2 flex items-center gap-2">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 font-semibold ${overlayProfile.infoLabelClass} ${getTopicRankingColor(activeTopic.score ?? 5)}`}>
                            Topic {activeTopic.segmentNumber ?? '?'} · {activeTopic.score ?? '?'} / 10
                          </span>
                        </div>
                      )}
                      {activeTopic?.title && (
                        <div className={`font-headlines font-semibold leading-tight text-white ${overlayProfile.infoTitleClass}`}>
                          {stripHtmlText(activeTopic.title)}
                        </div>
                      )}
                      {activeTopicDescription && (
                        <div className={`mt-1 leading-snug text-[#CBD2DB] ${overlayProfile.bodyTextClass}`}>
                          {activeTopicDescription}
                        </div>
                      )}
                      {activeQuestion?.questionText && (
                        <div className="mt-2">
                          <div className={`font-body font-semibold uppercase tracking-[0.22em] text-[#FFB52B]/80 ${overlayProfile.infoLabelClass}`}>
                            Current Question
                          </div>
                          <div className={`mt-1 leading-snug text-[#F4F4F4] ${overlayProfile.bodyTextClass}`}>
                            {stripHtmlText(activeQuestion.questionText)}
                          </div>
                        </div>
                      )}
                      {activeClip && (
                        <div className="mt-2 border-t border-white/10 pt-2">
                          <div className={`font-body font-semibold uppercase tracking-[0.22em] text-[#FFB52B]/80 ${overlayProfile.infoLabelClass}`}>
                            Current Clip
                          </div>
                          <div className={`mt-1 font-medium text-white ${overlayProfile.bodyTextClass}`}>
                            {stripHtmlText(activeClip.title) || 'Untitled clip'}
                          </div>
                          {activeClip.clipType && (
                            <div className={`mt-0.5 text-[#D5D9E0] ${overlayProfile.metaTextClass}`}>
                              {stripHtmlText(activeClip.clipType)}
                            </div>
                          )}
                          {activeClip.description && (
                            <div className={`mt-1 leading-snug text-[#CBD2DB] ${overlayProfile.metaTextClass}`}>
                              {stripHtmlText(activeClip.description)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`rounded-full border border-white/15 bg-[#0B1320]/70 font-mono text-white shadow-[0_16px_32px_-24px_rgba(0,0,0,0.72)] backdrop-blur-md ${overlayProfile.timerClass}`}>
                      {formatClockTime(playbackTime)} / {formatClockTime(resolvedTotalDuration)}
                    </div>
                  </div>
                  {activeSubtitleCue && (
                    <div className={`pointer-events-none absolute inset-x-0 flex justify-center ${overlayProfile.subtitleWrapperClass}`}>
                      <div
                        className={`border border-white/10 bg-black/58 text-center font-medium leading-snug text-white shadow-[0_24px_48px_-32px_rgba(0,0,0,0.82)] backdrop-blur-md ${overlayProfile.subtitleCardClass}`}
                        style={{ maxWidth: `${overlayProfile.subtitleMaxWidth}px` }}
                      >
                        {activeSubtitleCue.lines.map((line, index) => (
                          <div key={`${activeSubtitleCue.start}-${index}`}>{line}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Minimized state */}
      {isMinimized && (
        <div
          className={`px-2 py-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
          onClick={onToggleMinimize}
          style={{ cursor: 'pointer' }}
        >
          Click to expand
        </div>
      )}

      {/* Resize handle for floating mode */}
      {isFloating && !isMinimized && (
        <div
          onMouseDown={handleResizeMouseDown}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.5) 50%)',
          }}
          title="Drag to resize"
        />
      )}
    </div>
  );
};

export default HostedVideoPlayer;
